import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Alert,
  AlertTitle,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import GoogleLoginButton from '../features/auth/components/GoogleLoginButton';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleLoginSuccess = () => {
    navigate('/publishers');
  };

  const handleLoginError = (error: string) => {
    setLoginError(error);
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          mb: 4,
        }}
      >
        <img 
          src="/location-station-banner.svg" 
          alt="Location Station Logo" 
          style={{ maxWidth: '100%', height: 'auto', marginBottom: '1rem' }}
        />
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 600, color: 'primary.main', my: 4 }}
        >
          CityHub
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
        Welcome to CityHub, your all-in-one management portal for operating and customizing Location Station in your city! Manage websites, publishers, journalists, and advertisers with powerful customization features tailored for your local market.
        </Typography>
      </Box>

      <Card sx={{ mb: 4, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {isAuthenticated ? (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                <AlertTitle>Logged In</AlertTitle>
                You are logged in as {user?.name} ({user?.email})
              </Alert>
              <Typography variant="body1" paragraph>
              Welcome to CityHub, your all-in-one management portal for operating and customizing Location Station in your city. Manage websites, publishers, journalists, and advertisers with powerful customization features tailored for your local market.
              </Typography>
              <Button
                variant="contained"
                size="large"
                color="primary"
                onClick={() => navigate('/publishers')}
                fullWidth
                sx={{ mt: 2 }}
              >
                Go to Dashboard
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body1" paragraph>
                Please sign in with your Google account to access the Location Station CityHub.
                Only authorized publishers will be granted access.
              </Typography>
              
              {loginError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {loginError}
                </Alert>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <GoogleLoginButton
                  onSuccess={handleLoginSuccess}
                  onError={handleLoginError}
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
              Publishers
              </Typography>
              <Typography variant="body2" color="text.secondary">
              Manage publishers efficiently to represent your city's diverse voices on Location Station.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
              Location Station Studio
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customize each website's unique style and voice.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
              Advertisers
              </Typography>
              <Typography variant="body2" color="text.secondary">
              Easily integrate and manage advertisers to support and grow your local ecosystem.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Home;